import React, { useCallback } from 'react';
import { ReactComponent as JobTitleIcon } from './icons/job-title.svg';
import { ReactComponent as CompanyNameIcon } from './icons/company-name.svg';
import { ReactComponent as CompanyIcon } from 'assets/icons/business-outlined.svg';
import styles from './VoyagerFiltersMenu.module.css';
import CheckBox from '../../../../../../../common/components/Checkbox';
import { uniqueId } from 'lodash';
import classNames from 'classnames';
import { selectVoyagerFilterAttributes } from '@mm/state/selectors/voyagerLeadsSelectors';
import { Divider, Spin } from 'antd';
import { Section } from '../../LeadsSearch/LeadsSearchFiltersMenu/Section/Section';
import { Subsection } from '../../LeadsSearch/LeadsSearchFiltersMenu/Subsection/Subsection';
import {
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  VoyagerKeyType,
  VoyagerLeadsSearchQuery,
  VOYAGER_FRIENDLY_NAMES,
  VOYAGER_KEYS,
} from '@mm/state/interfaces/voyagerLeads';
import { useSelector } from 'react-redux';
import {
  LOCATION_KEYS,
  VoyagerRemoteFiltersSearch,
} from '../VoyagerRemoteFiltersSearch/VoyagerRemoteFiltersSearch';
import { SearchContacts } from './SearchContacts/SearchContacts';
import { useVoyagerFilters } from '../hooks/use-voyager-filters';

export const MAX_QUERY_ATTRIBUTES_LENGTH = 25;

type Props = {
  voyagerSearchQuery: VoyagerLeadsSearchQuery;
  setVoyagerSearchQuery: (params: VoyagerLeadsSearchQuery) => void;
  getUpdatedQuery: ReturnType<typeof useVoyagerFilters>['getUpdatedQuery'];
  isValueExists: ReturnType<typeof useVoyagerFilters>['isValueExists'];
  busy: boolean;
};

export const VoyagerFiltersMenu = ({
  voyagerSearchQuery,
  setVoyagerSearchQuery,
  getUpdatedQuery,
  isValueExists,
  busy,
}: Props) => {
  const attributes = useSelector(selectVoyagerFilterAttributes);

  const onCheckboxClick = useCallback(
    (key: VoyagerKeyType, value: string, operator?: 'not_in') => {
      setVoyagerSearchQuery(
        getUpdatedQuery(voyagerSearchQuery, key, value, operator),
      );
    },
    [setVoyagerSearchQuery, getUpdatedQuery, voyagerSearchQuery],
  );

  const renderCheckbox = useCallback(
    (key: VoyagerKeyType, label: string) => {
      const isChecked = isValueExists(key, label);

      return (
        <CheckBox
          key={uniqueId()}
          label={label}
          name={key}
          isChecked={isChecked}
          disabled={false}
          className={classNames(styles.checkbox)}
          labelClassName={styles.checkboxLabel}
          onClick={() => onCheckboxClick(key, label)}
        />
      );
    },
    [onCheckboxClick, isValueExists],
  );

  const renderCheckboxList = useCallback(
    (key: VoyagerKeyType) =>
      attributes?.map(attr => {
        if (attr.attribute === key) {
          return attr.items.map(c => renderCheckbox(key, c));
        }
        return null;
      }),
    [renderCheckbox, attributes],
  );

  const withSpin = (children: JSX.Element | JSX.Element[]) =>
    busy ? (
      <Spin size="small" spinning={busy} className={styles.spin}>
        {children}
      </Spin>
    ) : (
      children
    );

  const { query } = voyagerSearchQuery || {};

  const hasFiltersByAttribute = (keys: VoyagerKeyType | VoyagerKeyType[]) =>
    !!query.find(query =>
      Array.isArray(keys) && query.field
        ? keys.includes(query.field)
        : query.field === keys,
    );

  return (
    <div className={styles.container}>
      <div className={styles.title}>Filters</div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <SearchContacts
            className={classNames({ [styles.disabled]: busy })}
            onSearch={value => {
              setVoyagerSearchQuery(
                getUpdatedQuery(voyagerSearchQuery, undefined, value, 'search'),
              );
            }}
          />
        </div>

        <Section
          name={VOYAGER_FRIENDLY_NAMES.JOB_TITLE}
          icon={<JobTitleIcon />}
          hasActiveFilters={hasFiltersByAttribute([
            VOYAGER_KEYS.jobTitle,
            VOYAGER_KEYS.seniorityLevel,
            VOYAGER_KEYS.department,
          ])}
        >
          <VoyagerRemoteFiltersSearch
            attribute={VOYAGER_KEYS.jobTitle}
            placeholder={VOYAGER_FRIENDLY_NAMES.JOB_TITLE}
            query={query}
            onSelect={onCheckboxClick}
          />
          <Divider className={styles.divider} />
          <Subsection
            name={VOYAGER_FRIENDLY_NAMES.SENIORITY_LEVEL}
            className={styles.subsection}
            hasActiveFilters={hasFiltersByAttribute(
              VOYAGER_KEYS.seniorityLevel,
            )}
          >
            {withSpin(
              <div>{renderCheckboxList(VOYAGER_KEYS.seniorityLevel)}</div>,
            )}
          </Subsection>
          <Subsection
            name={VOYAGER_FRIENDLY_NAMES.DEPARTMENT}
            className={styles.subsection}
            hasActiveFilters={hasFiltersByAttribute(VOYAGER_KEYS.department)}
          >
            {withSpin(<div>{renderCheckboxList(VOYAGER_KEYS.department)}</div>)}
          </Subsection>
        </Section>
        <Section
          name="Location"
          icon={<EnvironmentOutlined className={styles.sectionIcon} />}
          hasActiveFilters={hasFiltersByAttribute(LOCATION_KEYS)}
        >
          <VoyagerRemoteFiltersSearch
            isLocation
            placeholder="City, State, Zip Code"
            onSelect={onCheckboxClick}
            lengthLimit={2}
            query={query}
          />
          <div className={styles.locationsBlock}>
            <div className={styles.heading}>Excluding Locations</div>
            <VoyagerRemoteFiltersSearch
              isLocation
              exclude
              placeholder="City, State, Zip Code"
              onSelect={onCheckboxClick}
              query={query}
            />
          </div>
        </Section>
        <Section
          name={VOYAGER_FRIENDLY_NAMES.COMPANY_NAME}
          icon={<CompanyNameIcon />}
          hasActiveFilters={hasFiltersByAttribute(VOYAGER_KEYS.companyName)}
        >
          <VoyagerRemoteFiltersSearch
            attribute={VOYAGER_KEYS.companyName}
            placeholder={VOYAGER_FRIENDLY_NAMES.COMPANY_NAME}
            query={query}
            onSelect={onCheckboxClick}
          />
        </Section>
        <Section
          name={VOYAGER_FRIENDLY_NAMES.PRIMARY_INDUSTRY}
          icon={<CompanyIcon className={styles.sectionIcon} />}
          hasActiveFilters={hasFiltersByAttribute([
            VOYAGER_KEYS.industry,
            VOYAGER_KEYS.domain,
          ])}
        >
          <VoyagerRemoteFiltersSearch
            attribute={VOYAGER_KEYS.industry}
            placeholder={VOYAGER_FRIENDLY_NAMES.PRIMARY_INDUSTRY}
            query={query}
            onSelect={onCheckboxClick}
            useStateResults
          />
          <VoyagerRemoteFiltersSearch
            attribute={VOYAGER_KEYS.domain}
            placeholder={VOYAGER_FRIENDLY_NAMES.COMPANY_DOMAIN}
            query={query}
            onSelect={onCheckboxClick}
          />
        </Section>
        <Section
          name={VOYAGER_FRIENDLY_NAMES.COMPANY_REVENUE}
          icon={<DollarOutlined className={styles.sectionIcon} />}
          hasActiveFilters={hasFiltersByAttribute(VOYAGER_KEYS.revenue)}
        >
          {withSpin(<div>{renderCheckboxList(VOYAGER_KEYS.revenue)}</div>)}
        </Section>
        <Section
          name={VOYAGER_FRIENDLY_NAMES.COMPANY_EMPLOYEE_COUNT}
          icon={<TeamOutlined className={styles.sectionIcon} />}
          hasActiveFilters={hasFiltersByAttribute(VOYAGER_KEYS.employees)}
        >
          {withSpin(<div>{renderCheckboxList(VOYAGER_KEYS.employees)}</div>)}
        </Section>
      </div>
    </div>
  );
};
